import React, { useState, useEffect } from 'react';
import RevolutCheckoutForm from './CheckoutForm';
import { post } from '../../../util/api';
import { revolutOrderCacheKey, getOrCreateRevolutOrder } from './revolutOrderCache';
import { useIntl } from 'react-intl';
import { errorLogger } from '../../../util/error.logger';
import {
  BOOKING_EVENTS,
  buildBookingSnapshot,
  trackBookingProgress,
} from '../../../util/bookingProgress';

const RevolutForm = ({
  basePrice,
  amount,
  restAmount,
  isAdvancedExperienceBooking,
  callbackFunction,
  termsAndConditions,
  elbwalkerPrice,
  isEarlyExperienceBooking,
  isSecondPayment,
  transaction,
  transitionName,
  startDate,
  endDate,
  seats,
  guests,
  listingId,
  userId,
  paymentDescription,
  isGiftCard,
  listing,
  singleBedroom,
}) => {
  const [publicId, setPublicId] = useState('');
  const [revolutOrderId, setRevolutOrderId] = useState('');
  const [revolutMode, setRevolutMode] = useState('sandbox');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intl = useIntl();
  const { locale } = intl;

  const purchaseAmount = isSecondPayment ? Math.round(amount / 100) : Math.round(amount);

  // `amount` arrives in euros on a first payment and in cents on a second one
  // (see purchaseAmount above); the booking record always stores minor units.
  const amountInCents = isSecondPayment ? Math.round(amount) : Math.round(amount * 100);

  // A gift card purchase is not a booking, so it stays out of this record.
  const trackPayment = (event, extra = {}) => {
    if (isGiftCard) return;
    trackBookingProgress(event, {
      booking: buildBookingSnapshot(listing || transaction?.listing, {
        bookingDates: { startDate, endDate },
        seats,
        guests,
        singleBedroom,
      }),
      payment: { provider: 'revolut', amount: amountInCents, currency: 'EUR', ...(extra.payment || {}) },
      ...(extra.error ? { error: extra.error } : {}),
      ...(transaction?.id?.uuid ? { transactionId: transaction.id.uuid } : {}),
    });
  };

  const data = {
    // Lets CheckoutForm invalidate the shared order cache once this order is
    // paid, so a later checkout can't reuse an already-paid order.
    orderCacheKey: revolutOrderCacheKey({
      transactionId: transaction?.id?.uuid,
      listingId: listingId || transaction?.listingId?.uuid,
      amount: purchaseAmount,
      isSecondPayment,
    }),
    amount,
    basePrice,
    restAmount,
    isAdvancedExperienceBooking,
    callbackFunction,
    termsAndConditions,
    elbwalkerPrice,
    isEarlyExperienceBooking,
    isSecondPayment,
    transaction,
    transitionName,
    startDate,
    endDate,
    seats,
    guests,
    isGiftCard,
    listing,
    singleBedroom,
    trackPayment,
  };

  useEffect(() => {
    const createRevolutOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        // The desktop and mobile layouts both mount this form; the shared cache
        // makes them use ONE Revolut order instead of minting one each.
        const cacheKey = revolutOrderCacheKey({
          transactionId: transaction?.id?.uuid,
          listingId: listingId || transaction?.listingId?.uuid,
          amount: purchaseAmount,
          isSecondPayment,
        });
        const response = await getOrCreateRevolutOrder(cacheKey, () =>
          post('/api/revolut/create-order', {
            amount: purchaseAmount,
            currency: 'EUR',
            description:
              paymentDescription ||
              intl.formatMessage({
                id: isSecondPayment ? 'RevolutForm.secondPayment' : 'RevolutForm.firstPayment',
              }),
            isSecondPayment: isSecondPayment,
            // Server-side linkage: the order's merchant reference carries the
            // booking context, and balance orders get tracked on the
            // transaction's metadata for reconciliation.
            transactionId: transaction?.id?.uuid,
            listingId: listingId || transaction?.listingId?.uuid,
            userId: userId || transaction?.customer?.id?.uuid,
          })
        );
        setPublicId(response.publicId);
        setRevolutOrderId(response.orderId);
        setRevolutMode(response.mode);
        setLoading(false);
        trackPayment(BOOKING_EVENTS.PAYMENT_STARTED, {
          payment: { orderId: response.orderId },
        });
      } catch (err) {
        errorLogger(err, {
          event: 'revolut-create-order-failed',
          file: 'RevolutForm/RevolutForm.js',
          amount: purchaseAmount,
          basePrice,
          restAmount,
          isSecondPayment,
          isEarlyExperienceBooking,
          isAdvancedExperienceBooking,
          transactionId: transaction?.id?.uuid,
          transitionName,
          listingId: listingId || transaction?.listingId?.uuid,
          userId: userId || transaction?.customer?.id?.uuid,
          startDate,
          endDate,
          seats,
          guests,
          errorDetails: err?.message || 'Failed to create payment order',
        });
        trackPayment(BOOKING_EVENTS.PAYMENT_FAILED, {
          error: {
            step: 'revolut-create-order',
            code: err?.name,
            message: err?.message || 'Failed to create payment order',
          },
        });
        setError(err.message || 'Failed to create payment order');
        setLoading(false);
      }
    };

    if (purchaseAmount && purchaseAmount > 0) {
      createRevolutOrder();
    } else if (amount !== undefined && amount !== null) {
      const invalidAmountError = new Error('Invalid payment amount');
      errorLogger(invalidAmountError, {
        event: 'revolut-invalid-payment-amount',
        file: 'RevolutForm/RevolutForm.js',
        amount,
        purchaseAmount,
        basePrice,
        restAmount,
        isSecondPayment,
        transactionId: transaction?.id?.uuid,
        listingId: listingId || transaction?.listingId?.uuid,
        userId: userId || transaction?.customer?.id?.uuid,
        startDate,
        endDate,
        seats,
        guests,
      });
      setError('Invalid payment amount');
      setLoading(false);
    }
  }, [purchaseAmount, isSecondPayment, amount, locale, paymentDescription]);

  if (loading) {
    return <div>Loading payment form...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <RevolutCheckoutForm
      data={data}
      publicId={publicId}
      revolutOrderId={revolutOrderId}
      mode={revolutMode}
      locale={locale}
      listingId={listingId}
      userId={userId}
    />
  );
};

export default RevolutForm;
